import type { FormTab, IFormGroup, IFormPatch, IRecordTypeDefinition, IRecordTypeOverride } from '@platform/core/types';
import { doubled, editorTabs, fullMark, isForcedFull, isKb, keyToggles, tabOrder, tagsKeyOf, titleEn, visibleFields } from './formRules';
import { autoWidthOf, withWidths } from './formWidths';
import type { IProtoForm } from './IProtoForm';
import { savedGroupsOf } from './kbGroups';

const rank = (tab: string) => { const i = tabOrder.indexOf(tab); return i < 0 ? tabOrder.length : i; };

/** Вкладки форми: вкладки, яких редактор не вмикає (Загальне, Читання, Дочірні…), лишаються; увімкнені — на своїх місцях. Чиста функція. */
export function tabsOf(current: readonly FormTab[], on: IProtoForm['tabs']): FormTab[] {
  const kept = current.filter((t) => !(editorTabs as readonly string[]).includes(t));
  return [...new Set<FormTab>([...kept, ...editorTabs.filter((t) => on[t])])].sort((a, b) => rank(a) - rank(b));
}

/**
 * Повна форма з моделі редактора (патч `forms.full`, ADR-023) так, як її показує запис прототипу v4: ключовий рядок — виконавець і
 * увімкнені статус, пріоритет, термін (у базі знань — лише статус); під назвою — назва, теги, опис (не в базі знань); вкладки; групи —
 * у полях групи немає виконавця (запис показує його в ключовому рядку), у розкладці є (місце в редакторі); ширина — явна або «Авто»,
 * у «Подробицях» подвоєна; групи «Подробиці», «Нотатки», «Опис», «Зв’язки» — завжди на весь рядок (їхнє «Увесь рядок» — відмітка
 * fullMark у розкладці). `published` — опубліковане визначення типу (ширина «Авто»). Чиста функція.
 */
export function formPatchOf(rt: IRecordTypeDefinition, proto: IProtoForm, published?: IRecordTypeDefinition): IFormPatch {
  const fields = visibleFields(rt);
  const byKey = new Map(fields.map((f) => [f.key, f]));
  const kb = isKb(rt);
  const tags = tagsKeyOf(fields);
  const keyLine = [...(byKey.has('assignee') ? ['assignee'] : []), ...keyToggles.filter((k) => byKey.has(k) && proto.key[k] && !(kb && k !== 'status'))];
  const underTitle = ['title', ...(tags && proto.showTags ? [tags] : []), ...(!kb && proto.showDesc && byKey.has('description') ? ['description'] : [])];
  const groups = savedGroupsOf(rt, proto).map((g): IFormGroup => {
    const keys = g.keys.filter((k) => byKey.has(k));
    const width = (k: string) => {
      const w = proto.widths[k] ?? autoWidthOf(byKey.get(k)!, published);
      return titleEn(g.title) === 'Details' ? doubled(w) : w;
    };
    const forced = isForcedFull(g.title);
    return {
      id: g.id, title: g.title, full: g.full || forced, fields: keys.filter((k) => k !== 'assignee'),
      layout: [...keys.map((k) => ({ key: k, width: width(k) })), ...(forced && g.full ? [{ key: fullMark }] : [])],
    };
  });
  return { keyLine, underTitle, groups, tabs: tabsOf(rt.form.tabs, proto.tabs) };
}

/**
 * Перевизначення з формою редактора: `forms.full` — форма моделі (інші варіанти без змін), вкладки «Пов’язані» й «Історія» конструктора
 * (`tabs`) вилучаються — повна форма сама несе вкладки; явні ширини полів — у перевизначеннях полів. Чиста функція.
 */
export function withProtoForm(patch: IRecordTypeOverride, rt: IRecordTypeDefinition, proto: IProtoForm, published?: IRecordTypeDefinition): IRecordTypeOverride {
  const out = withWidths(patch, proto.widths);
  out.forms = { ...(patch.forms ?? {}), full: formPatchOf(rt, proto, published) };
  delete out.tabs;
  return out;
}
